/* eslint-disable no-alert, prefer-template */

import { Plugins } from '@capacitor/core';
import '@/webPlugins/PurchasesPlugin';
import analytics from '@/utils/Analytics';
import localStore from '@/utils/LocalStore';

const { PurchasesPlugin: Purchases } = Plugins;

const publicKey = 'aYXhdgWLRXsdJnzAQiBLSRkinYlPdmCO';
const entitlementId1 = 'membership_level1';
const entitlementId2 = 'membership_level2';
const monthlyPackageId1 = 'monthly_level1';
const monthlyPackageId2 = 'monthly_level2';

/*

  STATE

 */

const state = {
  subscription: {},
  monthlyPackage1: null,
  monthlyPackage2: null,
  canMakePurchases: null,
  subcriptionAfterPurchase: null, // Only using for testing, delete later
  purchaseError: null,
  packagesError: null,
  showNewMemberWelcome: false,
};

/*

  GETTERS

 */

const getters = {
  // Includes users with current or expired subscriptions
  hasSubscribedInPast: state => state.isMember || 'identifier' in state.subscription,
};

/*

  ACTIONS

 */

const actions = {
  async init({ commit }, userId) {
    try {
      Purchases.setup({
        publicKey,
        userId,
        entitlementId1,
        entitlementId2,
        monthlyPackageId1,
        monthlyPackageId2,
      });
    } catch (error) {
      console.error('Error setting up Purchases:', error);
      analytics.exception('Error setting up Purchases:' + error.toString());
    }
    const showNewMemberWelcome = await localStore.getItem('showNewMemberWelcome');
    commit('setShowNewMemberWelcome', showNewMemberWelcome);
  },

  // Called once on user init and when opening Paywall or Membership settings
  async checkSubscriptionStatus({ dispatch }, savedMemberLevel) {
    // Don't trigger for localhost reloads
    // if (process.env.NODE_ENV === 'development') {
    //   console.warn('[Dev mode] Not calling user/checkSubscriptionStatus during development');
    //   return;
    // }

    try {
      const { memberLevel = 0, subscription = {} } = await Purchases.getPurchaserInfo();
      // console.log('SUBSCRIPTION INFO', { memberLevel, subscription });
      dispatch('updateSavedSubscription', { memberLevel, subscription });
      if (savedMemberLevel !== memberLevel) {
        dispatch('user/reinitAfterSubscriptionChange', memberLevel > 0, { root: true });
      }
    } catch (error) {
      // alert('Error getting purchaserInfo:', error.toString());
      console.error('Error getting purchaserInfo:', error);
      dispatch('updateSavedSubscription', { memberLevel: savedMemberLevel });
      analytics.exception(`Error getting purchaserInfo: ${error}`);
    }
  },

  async updateSavedSubscription({ commit, dispatch }, { memberLevel, subscription }) {
    commit('user/setMembership', { isMember: memberLevel > 0, memberLevel }, { root: true });

    if (subscription) {
      commit('setSubscription', subscription);
      dispatch(
        'user/saveUserInitDataToDB',
        { moduleName: 'purchases', moduleData: { memberLevel, subscription } },
        { root: true }
      );
    }
    analytics.setSubscriberProperty(memberLevel);
  },

  async getPackages({ commit }) {
    commit('setPackagesError', null);
    try {
      const { monthlyPackage1, monthlyPackage2 } = await Purchases.getPackages();
      // console.log('PACKAGES TO BUY', monthlyPackage1, monthlyPackage2);
      commit('setPackages', { monthlyPackage1, monthlyPackage2 });
    } catch (error) {
      console.error('Error getting packages', error.message, error.code);
      commit('setPackagesError', error);
      // alert('getPackages Error:' + error.toString());
    }
  },

  async checkIfCanMakePurchases({ commit }) {
    try {
      const { canMakePurchases } = await Purchases.checkIfCanMakePurchases();
      commit('setCanMakePurchases', canMakePurchases);
    } catch (error) {
      // alert('checkIfCanMakePurchases Error:' + error);
      console.error(error);
    }
  },

  async purchasePackage({ commit, dispatch }, packageId) {
    commit('setPurchaseError', null);
    try {
      const { memberLevel = 0, subscription = {} } = await Purchases.purchasePackage({
        packageId,
      });
      commit('setSubcriptionAfterPurchase', subscription); // TESTING ONLY
      dispatch('updateSavedSubscription', { memberLevel, subscription });
      if (memberLevel > 0) {
        dispatch('saveShowNewMemberWelcome', true);
        dispatch('ui/updateSnack', { text: `Thank you! You're now a member` }, { root: true });
      }
      dispatch('user/reinitAfterSubscriptionChange', memberLevel > 0, { root: true });
    } catch (error) {
      // Paywall handles logging cancels and errors in analytics
      commit('setPurchaseError', error.message, error.code);
      console.error('Error purchasing package', error);
      // alert('Purchase Error:' + error.toString());
    }
  },

  async restorePurchases({ commit, dispatch }) {
    // TODO Restore purchase with dialog
    try {
      const { memberLevel = 0, subscription = {} } = await Purchases.restorePurchases();
      dispatch('ui/updateSnack', { text: 'Restored purchase' }, { root: true });
      dispatch('updateSavedSubscription', { memberLevel, subscription });
    } catch (error) {
      commit('setPurchaseError', error);
      // alert('restorePurchases Error:' + error.toString());
      console.error('restorePurchases Error:', error);
    }
  },

  // Called by auth/signOut
  reset() {
    Purchases.resetUser();
  },

  saveShowNewMemberWelcome({ commit }, bool) {
    commit('setShowNewMemberWelcome', bool);
    localStore.setItem('showNewMemberWelcome', bool);
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setSubscription(state, subscription = {}) {
    state.subscription = subscription;
  },

  setPackages(state, { monthlyPackage1, monthlyPackage2 }) {
    state.monthlyPackage1 = monthlyPackage1;
    state.monthlyPackage2 = monthlyPackage2;
  },

  setPackagesError(state, error) {
    state.packagesError = error;
  },

  setCanMakePurchases(state, canMakePurchases) {
    state.canMakePurchases = canMakePurchases;
  },

  // TESTNG - Can delete later
  setSubcriptionAfterPurchase(state, subscription) {
    state.subcriptionAfterPurchase = subscription;
  },

  setPurchaseError(state, error) {
    state.purchaseError = error;
  },

  setShowNewMemberWelcome(state, bool) {
    state.showNewMemberWelcome = bool;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
